import React, { useEffect, useMemo, useState } from "react";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Section, PrimaryButton } from "@/components/ui-helpers";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Lightbox from "@/components/Lightbox";
import { api, mediaUrl } from "@/lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null); // index within `filtered`
  useEffect(() => { api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = useMemo(() => (cat === "All" ? items : items.filter((i) => i.category === cat)), [items, cat]);

  return (
    <>
      <SEO title="Before & After Gallery" description="Honest before-and-after results from real Ascendia projects across Israel." />
      <PageHero kicker="Before & After" title="Real projects. Honest results." subtitle="These are authentic Ascendia project photos, shown exactly as taken — before and after, never altered.">
        <PrimaryButton to="/contact">Request a Quote <ArrowRight className="h-4 w-4" /></PrimaryButton>
      </PageHero>
      <Section>
        <Tabs value={cat} onValueChange={(v) => { setCat(v); setActive(null); }} data-testid="gallery-category-tabs">
          <TabsList className="flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
            {cats.map((c) => (
              <TabsTrigger key={c} value={c} className="rounded-full border border-[color:var(--border-subtle)] bg-white/[0.02] px-4 py-1.5 text-sm data-[state=active]:bg-gold data-[state=active]:text-black text-secondary">{c}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="mt-10 gap-6 sm:columns-2 lg:columns-2 [column-fill:_balance]">
          {filtered.map((g, i) => (
            <button
              key={g.id}
              onClick={() => setActive(i)}
              data-testid="gallery-item-card"
              className="ascendia-card mb-6 block w-full break-inside-avoid overflow-hidden cursor-pointer group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]"
            >
              <div className="relative overflow-hidden">
                <img src={mediaUrl(g.image)} alt={g.title} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-[420ms] ease-[var(--ease-out)] group-hover:scale-[1.04]" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-70 transition-opacity duration-[240ms] group-hover:opacity-85" />
                <span className="absolute top-3 left-3 z-10 text-[10px] font-mono uppercase tracking-[0.16em] bg-black/70 text-gold-soft px-2.5 py-1 rounded-full border border-[color:var(--border-strong)] backdrop-blur">Before / After</span>
                <span className="absolute top-3 right-3 z-10 text-[10px] uppercase tracking-[0.16em] bg-black/60 text-white px-2.5 py-1 rounded-full border border-white/10">{g.category}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold text-white u-tracking-tight">{g.title}</h3>
                <p className="mt-1 text-xs text-muted-c flex items-center gap-1"><MapPin className="h-3 w-3" /> {g.location} · {g.service}</p>
              </div>
            </button>
          ))}
        </div>
        {filtered.length === 0 && <p className="mt-10 text-center text-muted-c">No projects in this category yet.</p>}

        <Lightbox items={filtered} index={active} onClose={() => setActive(null)} onIndexChange={setActive} />
      </Section>
    </>
  );
}
